import { MEDIA_TYPE, VIDEO_TYPE } from '../base/media';
import { isLocalParticipantPresenter } from '../base/participants/functions';
import { StateListenerRegistry } from '../base/redux';
import { getLocalTracks, replaceLocalTrack } from '../base/tracks';
import { selectParticipantInLargeVideo } from '../large-video/actions';
import { NOTIFICATION_TIMEOUT_TYPE, showNotification } from '../notifications';
import { stopLocalVideoRecording } from '../recording/actions.any';
import LocalRecordingManager from '../recording/components/Recording/LocalRecordingManager';
import { muteLocal } from '../video-menu/actions.any';
import { goBackstage, setStartBackstage } from './actions';
import { getWebinarAudienceLargeVideoParticipants, isAudienceStageOnlyModeActive, isLocalRecordingAllowedForLocalParticipant, isMediaBlockedForLocal, shouldApplyStartBackstage } from './functions';
/**
 * Backstage presence is shown in the conference header (same pattern as the
 * screenshare badge). The old sticky notification / floating banner path is
 * intentionally not used anymore so the indicator stays in one place.
 */
/**
 * Applies the moderator's prejoin "start in backstage" choice once they are
 * genuinely in the main meeting (prejoin gone, conference joined, moderator
 * confirmed). One-shot: the choice is cleared before the switch so leaving
 * backstage later does not bounce them back in.
 */
StateListenerRegistry.register(
/* selector */ state => shouldApplyStartBackstage(state), 
/* listener */ (apply, { dispatch }) => {
    if (apply) {
        dispatch(setStartBackstage(false));
        dispatch(goBackstage());
    }
});
/**
 * Locks the microphone and camera the moment the local participant becomes a
 * presenter (share-only role). The mic and camera tracks are removed from the
 * conference entirely (not just muted), so no audio/video source is ever
 * signaled to Jicofo and the bridge never routes presenter A/V. The desktop
 * (screen-share) track is untouched. The webinar middleware blocks any later
 * unmute attempt; this listener handles the transition itself, when they may
 * already be unmuted.
 */
StateListenerRegistry.register(
/* selector */ state => isLocalParticipantPresenter(state), 
/* listener */ (presenter, { dispatch, getState }) => {
    if (presenter) {
        dispatch(muteLocal(true, MEDIA_TYPE.AUDIO));
        dispatch(muteLocal(true, MEDIA_TYPE.VIDEO));
        for (const track of getLocalTracks(getState()['features/base/tracks'])) {
            if (track.mhTrack
                && (track.mediaType === MEDIA_TYPE.AUDIO
                    || (track.mediaType === MEDIA_TYPE.VIDEO && track.videoType !== VIDEO_TYPE.DESKTOP))) {
                dispatch(replaceLocalTrack(track.mhTrack, null));
            }
        }
        dispatch(showNotification({
            titleKey: 'webinar.presenterTitle',
            descriptionKey: 'webinar.presenterDescription'
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    }
});
/**
 * Enforces the moderator-set local-recording policy on an already-running local
 * recording: when the policy flips to disallowed, stop it and tell the user why.
 */
StateListenerRegistry.register(
/* selector */ state => !state['features/base/config'].iAmRecorder
    && !isLocalRecordingAllowedForLocalParticipant(state), 
/* listener */ (blocked, { dispatch }) => {
    var _a;
    // Optional-chained: LocalRecordingManager.native.ts is an empty module,
    // so the default import is undefined on mobile.
    if (blocked && ((_a = LocalRecordingManager === null || LocalRecordingManager === void 0 ? void 0 : LocalRecordingManager.isRecordingLocally) === null || _a === void 0 ? void 0 : _a.call(LocalRecordingManager))) {
        dispatch(stopLocalVideoRecording());
        dispatch(showNotification({
            titleKey: 'webinar.localRecordingStoppedTitle',
            descriptionKey: 'webinar.localRecordingStoppedDescription'
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    }
});
/**
 * Enforces "block everyone's mic and camera" on this client. Keyed on the
 * derived flag rather than the raw metadata so it only fires once the local
 * role is known: a moderator joining a blocked room is never muted by a
 * snapshot that arrived a moment before their role did, and a participant is
 * muted exactly once when the block starts (or when they arrive under it --
 * a late joiner or a promoted visitor gets the same snapshot from the room).
 *
 * Lifting the block does not unmute anyone; the buttons come back and the
 * person unmutes when they choose to.
 */
StateListenerRegistry.register(
/* selector */ state => isMediaBlockedForLocal(state), 
/* listener */ (blocked, { dispatch }) => {
    if (!blocked) {
        return;
    }
    dispatch(muteLocal(true, MEDIA_TYPE.AUDIO));
    dispatch(muteLocal(true, MEDIA_TYPE.VIDEO));
    dispatch(showNotification({
        titleKey: 'webinar.mediaBlockAllTitle',
        descriptionKey: 'webinar.mediaBlockAllDescription'
    }, NOTIFICATION_TIMEOUT_TYPE.LONG));
});
/**
 * Re-elects the large-video participant whenever the viewer-facing participant
 * set changes. This also clears a previously selected hidden participant when
 * stage-only mode has no active screen share or moderator pin.
 */
StateListenerRegistry.register(state => {
    if (!isAudienceStageOnlyModeActive(state)) {
        return undefined;
    }
    return getWebinarAudienceLargeVideoParticipants(state)
        .map(participant => participant.id)
        .join('|');
}, (_, { dispatch }) => {
    dispatch(selectParticipantInLargeVideo());
});
